import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Eye, EyeOff, Loader2, Unlock, Users } from "lucide-react";
import { toast } from "sonner";
import {
  useScheduleOptions,
  type ScheduleOptionWithCount,
} from "@/hooks/useScheduleOptions";
import FutureSessionMeter from "@/components/FutureSessionMeter";
import InterestedFamiliesDialog from "@/components/admin/classes/InterestedFamiliesDialog";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface Props {
  dbClassId: string | null | undefined;
}

/**
 * Admin manager for `class_schedule_options` — additional / proposed class
 * time slots (Monday LEGO Robotics interest option, etc.). Independent from
 * the class draft's Save button: each row is persisted immediately so admins
 * don't lose entries if they close the editor without saving the class draft.
 */
export default function ScheduleOptionsSection({ dbClassId }: Props) {
  const { options, loading, reload } = useScheduleOptions(dbClassId);
  const [adding, setAdding] = useState(false);

  if (!dbClassId) {
    return (
      <div className="rounded-md border p-3 text-xs text-muted-foreground">
        Save the class first to manage potential future sessions.
      </div>
    );
  }

  const addOption = async () => {
    setAdding(true);
    const { error } = await (supabase as any).from("class_schedule_options").insert({
      class_id: dbClassId,
      day_of_week: "Monday",
      start_time: "10:00 AM",
      end_time: null,
      status: "interest_only",
      display_label: "Monday at 10:00 AM",
      interest_threshold: 5,
      sort_order: options.length + 1,
    });
    setAdding(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    reload();
  };

  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40">
        <div>
          <div className="text-sm font-medium">Potential Future Sessions</div>
          <div className="text-xs text-muted-foreground">
            Propose additional class times without opening registration. Each option
            collects family interest and can be converted to open registration later.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={addOption} disabled={adding}>
          {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
          Add option
        </Button>
      </div>
      {loading && options.length === 0 ? (
        <p className="p-3 text-sm text-muted-foreground">Loading…</p>
      ) : options.length === 0 ? (
        <p className="p-3 text-sm text-muted-foreground">
          No potential future sessions yet. Add one to start collecting interest for a
          proposed time slot.
        </p>
      ) : (
        <div className="divide-y">
          {options.map((opt) => (
            <OptionRow key={opt.id} opt={opt} onChanged={reload} />
          ))}
        </div>
      )}
    </div>
  );
}

function OptionRow({
  opt,
  onChanged,
}: {
  opt: ScheduleOptionWithCount;
  onChanged: () => void;
}) {
  const [local, setLocal] = useState(opt);
  const [saving, setSaving] = useState(false);
  useEffect(() => setLocal(opt), [opt]);

  const dirty =
    local.day_of_week !== opt.day_of_week ||
    local.start_time !== opt.start_time ||
    (local.end_time ?? "") !== (opt.end_time ?? "") ||
    local.display_label !== opt.display_label ||
    local.interest_threshold !== opt.interest_threshold ||
    local.status !== opt.status;

  const save = async () => {
    setSaving(true);
    const { error } = await (supabase as any)
      .from("class_schedule_options")
      .update({
        day_of_week: local.day_of_week,
        start_time: local.start_time,
        end_time: local.end_time || null,
        display_label: local.display_label,
        interest_threshold: local.interest_threshold,
        status: local.status,
      })
      .eq("id", opt.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Option saved");
    onChanged();
  };

  const remove = async () => {
    if (!confirm(`Remove "${opt.display_label}"? Existing interest records will remain but no longer be linked.`)) return;
    const { error } = await (supabase as any)
      .from("class_schedule_options")
      .delete()
      .eq("id", opt.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Option removed");
    onChanged();
  };

  const toggleHidden = async () => {
    const next = local.status === "hidden" ? "interest_only" : "hidden";
    const { error } = await (supabase as any)
      .from("class_schedule_options")
      .update({ status: next })
      .eq("id", opt.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    onChanged();
  };

  const convertToOpen = async () => {
    if (!confirm(`Convert "${opt.display_label}" from Interest Only to OPEN registration? Families on the interest list will not be enrolled automatically.`)) return;
    const { error } = await (supabase as any)
      .from("class_schedule_options")
      .update({ status: "open" })
      .eq("id", opt.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Option opened");
    onChanged();
  };

  const reachedThreshold =
    opt.status === "interest_only" && opt.interest_count >= opt.interest_threshold;

  const [showFamilies, setShowFamilies] = useState(false);

  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant="outline"
          className={
            local.status === "interest_only"
              ? "bg-amber-50 text-amber-800 border-amber-200"
              : local.status === "open"
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : local.status === "closed"
                  ? "bg-neutral-100 text-neutral-700 border-neutral-200"
                  : "bg-slate-100 text-slate-600 border-slate-200"
          }
        >
          {local.status === "interest_only" ? "Interest Only" : local.status}
        </Badge>
        {reachedThreshold && (
          <Badge className="bg-emerald-100 text-emerald-900 border border-emerald-300 text-[10px]">
            Goal Reached
          </Badge>
        )}
      </div>

      <FutureSessionMeter count={opt.interest_count} threshold={opt.interest_threshold} />

      {reachedThreshold && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          This option has reached its interest goal and may be ready to open. Manual
          confirmation still required.
        </div>
      )}

      <div className="grid grid-cols-12 gap-2 items-start">
        <div className="col-span-3">
          <Label className="text-xs">Day</Label>
          <select
            value={local.day_of_week}
            onChange={(e) => setLocal({ ...local, day_of_week: e.target.value })}
            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Start</Label>
          <Input
            value={local.start_time}
            onChange={(e) => setLocal({ ...local, start_time: e.target.value })}
            placeholder="10:30 AM"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">End (optional)</Label>
          <Input
            value={local.end_time ?? ""}
            onChange={(e) => setLocal({ ...local, end_time: e.target.value })}
            placeholder="TBD"
          />
        </div>
        <div className="col-span-3">
          <Label className="text-xs">Public label</Label>
          <Input
            value={local.display_label}
            onChange={(e) => setLocal({ ...local, display_label: e.target.value })}
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Threshold</Label>
          <Input
            type="number"
            min={1}
            value={local.interest_threshold}
            onChange={(e) => setLocal({ ...local, interest_threshold: Number(e.target.value) || 0 })}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap pt-1">
        <Button size="sm" onClick={save} disabled={!dirty || saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          Save option
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowFamilies(true)}>
          <Users className="h-4 w-4 mr-1" /> View interested families ({opt.interest_count})
        </Button>
        <Button size="sm" variant="outline" onClick={toggleHidden}>
          {local.status === "hidden" ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
          {local.status === "hidden" ? "Show" : "Hide"}
        </Button>
        {local.status === "interest_only" && (
          <Button size="sm" variant="outline" onClick={convertToOpen}>
            <Unlock className="h-4 w-4 mr-1" /> Convert to Open
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={remove} className="text-destructive">
          <Trash2 className="h-4 w-4 mr-1" /> Remove
        </Button>
      </div>

      <InterestedFamiliesDialog
        open={showFamilies}
        onClose={() => setShowFamilies(false)}
        dbClassId={opt.class_id}
        displayLabel={opt.display_label}
        optionLabel={opt.display_label}
      />
    </div>
  );
}
